import React, { useEffect } from "react";
import { Route, Switch, Redirect, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Cookies from "universal-cookie";
import { useTranslation, Trans } from "react-i18next";

import { Copyright, Policies } from "@/_components";
import LanguageSelector from "../_components/LanguageSelector";

import Login from "./Login";
import VerifyEmail from "./VerifyEmail";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import Transfer from "./Transfer";

import { styled, useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";

import LifeSaferLogo from "@/_images/LSISA_Logo.webp";
import RoadWayImage from "@/_images/LSISA_Roadway.png";

import DriverImageOne from "@/_images/LSISA_TeenDriver1.webp";
import DriverImageTwo from "@/_images/LSISA_TeenDriver2.webp";

const AccountRoot = styled(Grid)(() => ({
  minHeight: "100vh",
  width: "100%",
  overflow: "hidden",
  backgroundColor: "#ffffff",
  position: "relative",
}));

const LeftPanel = styled(Grid)(({ theme }) => ({
  position: "relative",
  height: "100vh",
  minHeight: "100vh",
  overflow: "hidden",
  backgroundColor: "#ffffff",

  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}));

const HeroText = styled(Typography)(({ theme }) => ({
  position: "absolute",
  top: "16px",
  left: "12px",
  zIndex: 5,
  fontSize: "64px",
  lineHeight: "1.2",
  fontWeight: 900,
  letterSpacing: "-1px",
  color: "#1c355e",
  whiteSpace: "pre-line",

  [theme.breakpoints.down("lg")]: {
    fontSize: "58px",
  },

  [theme.breakpoints.down("md")]: {
    fontSize: "46px",
    lineHeight: "1.55",
  },
}));

const RoadWayImageStyle = styled("img")(({ theme }) => ({
  position: "absolute",
  top: "60px",
  bottom: 0,
  left: "-70px",
  height: "100vh",
  width: "auto",
  objectFit: "contain",
  objectPosition: "top left",
  zIndex: 1,
  pointerEvents: "none",
  transform: "scale(1.2)",

  [theme.breakpoints.down("lg")]: {
    left: "-75px",
    height: "100vh",
  },

  [theme.breakpoints.down("md")]: {
    left: "-190px",
    height: "100vh",
  },
}));

const FloatingImage = styled("img")(() => ({
  position: "absolute",
  objectFit: "cover",
  borderRadius: "6px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
  zIndex: 4,
}));

const FloatingImageOne = styled(FloatingImage)(({ theme }) => ({
  position: "absolute",
  width: "162px",
  height: "242px",
  right: "250px",
  top: "290px",

  [theme.breakpoints.down("xl")]: {
    right: "50px",
    bottom: "40px",
  },

  [theme.breakpoints.down("md")]: {
    width: "130px",
    height: "195px",
    left: "72px",
    top: "34px",
  },
}));

const FloatingImageTwo = styled(FloatingImage)(({ theme }) => ({
  position: "absolute",
  width: "162px",
  height: "242px",
  left: "300px",
  bottom: "220px",

  [theme.breakpoints.down("xl")]: {
    left: "300px",
    bottom: "60px",
  },

  [theme.breakpoints.down("md")]: {
    width: "130px",
    height: "195px",
    left: "245px",
    top: "255px",
  },
}));

const RightPanel = styled(Grid)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  height: "100vh",
  backgroundColor: "#ffffff",
  paddingRight: "28px",
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  overflowY: "auto",

  "@supports (height: 100dvh)": {
    minHeight: "100dvh",
    height: "100dvh",
  },

  [theme.breakpoints.down("sm")]: {
    paddingRight: 0,
  },
}));

const TopBar = styled(Grid)(({ theme }) => ({
  width: "100%",
  height: "58px",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  paddingRight: "28px",

  [theme.breakpoints.down("sm")]: {
    justifyContent: "flex-end",
    paddingRight: 0,
  },

  "@media (max-height: 700px)": {
    height: "48px",
  },

  "@media (max-height: 560px)": {
    height: "40px",
  },
}));

const LanguageSelectorHolder = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "isSmall",
})(({ isSmall }) => ({
  marginLeft: 0,
  paddingRight: isSmall ? 4 : 0,
}));

const LoginWrapper = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flex: "0 0 auto",
  marginTop: 0,
  paddingBottom: theme.spacing(2),

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },

  "@media (max-height: 700px)": {
    paddingBottom: theme.spacing(1),
  },
}));

const StyledLogo = styled("img")(({ theme }) => ({
  width: "300px",
  height: "auto",
  flexShrink: 0,
  marginTop: 0,
  marginBottom: "46px",

  [theme.breakpoints.down("sm")]: {
    width: "260px",
    marginTop: "20px",
    marginBottom: "36px",
  },

  "@media (max-height: 760px)": {
    width: "260px",
    marginBottom: "28px",
  },

  "@media (max-height: 660px)": {
    width: "220px",
    marginBottom: "18px",
  },

  "@media (max-height: 560px)": {
    width: "180px",
    marginTop: 0,
    marginBottom: "10px",
  },
}));

const FormHolder = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "624px",
  minHeight: 0,

  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
  },
}));

const FooterWrapper = styled(Grid)(({ theme }) => ({
  flexShrink: 0,
  marginTop: "auto",
  paddingBottom: "18px",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",

  [theme.breakpoints.down("sm")]: {
    paddingBottom: "12px",
  },

  "@media (max-height: 700px)": {
    paddingBottom: "8px",
  },

  "@media (max-height: 560px)": {
    "& .MuiTypography-root": {
      fontSize: "0.75rem",
      lineHeight: 1.25,
    },
  },
}));

function Account({ history, match }) {
  const { path } = match;
  const { pathname } = useLocation();
  const theme = useTheme();

  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const cookies = new Cookies();
  const user = cookies.get("driveSaferUser");

  useTranslation();

  const source = pathname.slice(9, 17);

  document.title = "LifeSaferISA - Drive Safer Portal";

  useEffect(() => {
    if (
      source !== "transfer" &&
      user !== undefined &&
      user.jwtToken !== undefined &&
      user.jwtToken !== null &&
      user.jwtToken.length > 20
    ) {
      history.push("/");
    } else if (source === "transfer") {
      const enrollmentCode = pathname.split("/").pop();

      if (enrollmentCode !== undefined) {
        cookies.set("ceCode", JSON.stringify(enrollmentCode), {
          path: "/",
          sameSite: "strict",
        });

        history.push(`${path}/transfer`);
      } else {
        cookies.remove("ceCode");
        history.push("/");
      }
    }
  }, []);

  return (
    <AccountRoot container component="main">
      {source === "transfer" ? (
        <Switch>
          <Route exact path={`${path}/transfer`} component={Transfer} />
        </Switch>
      ) : (
        <>
          <LeftPanel
            size={{
              sm: 12,
              lg: 7,
            }}
          >
            <HeroText variant="h2" align="left">
              <Trans
                i18nKey="carousel.loginText"
                components={{
                  colored: <span style={{ color: "#76bc21" }} />,
                }}
              />
            </HeroText>

            <RoadWayImageStyle src={RoadWayImage} alt="LifeSafer roadway" />

            <FloatingImageOne src={DriverImageOne} alt="Driver" />

            <FloatingImageTwo src={DriverImageTwo} alt="People driving" />
          </LeftPanel>

          <RightPanel
            component={Paper}
            elevation={0}
            square
            size={{
              sm: 12,
              lg: 5,
            }}
            sx={{ paddingRight: "10px", width: "100%" }}
          >
            <TopBar container>
              <LanguageSelectorHolder isSmall={isSmall}>
                <LanguageSelector />
              </LanguageSelectorHolder>
            </TopBar>

            <LoginWrapper>
              <StyledLogo src={LifeSaferLogo} alt="LifeSafer" />

              <FormHolder>
                <Switch>
                  <Route exact path={`${path}/login`} component={Login} />

                  <Route
                    exact
                    path={`${path}/verify-email`}
                    component={VerifyEmail}
                  />

                  <Route
                    exact
                    path={`${path}/forgot-password`}
                    component={ForgotPassword}
                  />

                  <Route
                    exact
                    path={`${path}/reset-password`}
                    component={ResetPassword}
                  />

                  <Redirect from="*" to={`${path}/login`} />
                </Switch>
              </FormHolder>
            </LoginWrapper>

            <FooterWrapper container>
              <Grid>
                <Policies />
              </Grid>

              <Grid>
                <Copyright />
              </Grid>
            </FooterWrapper>
          </RightPanel>
        </>
      )}
    </AccountRoot>
  );
}

Account.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object.isRequired,
};

export default Account;
